"use client";

import React from "react";
import { TrendingUp, Loader2, Info } from "lucide-react";

import { useLeadStats, usePipelineStages } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Badge } from "@/app/_module/components/ui/badge";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";

import { LEAD_STATUS_CONFIG } from "../types/status-definitions";

/**
 * Follows the dashboard's date filter.
 *
 * ⚠️ It did not, for a long time: this was rendered with no props and `/leads/stats`
 * accepted no dates, so the card silently reported ALL TIME while every number around it
 * respected the filter. Same agent, same day: 5,831 leads here vs 5,204 on the
 * leaderboard. Omitting the range still means all-time, which is what the Leads page and
 * the admin panel want.
 */
export const LeadsOverview = function ({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
} = {}) {
  const { isWhiteLabeled } = useBranding();

  const { data: pipelineStages } = usePipelineStages();

  const {
    data: stats,
    isLoading,
    error,
    refetch
  } = useLeadStats({ startDate, endDate });

  // Force refetch on component mount to ensure fresh data
  React.useEffect(() => {
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Leads Overview
          </CardTitle>
          <TrendingUp className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading leads data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Leads Overview
          </CardTitle>
          <TrendingUp className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load leads data. Please try again later.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const unwrap = (input: unknown): {
    totalLeads?: number;
    leadsByStatus?: Record<string, { count?: number }>;
    closingRate?: number;
    leadsWithConversations?: number;
    /** ISSUED only, same denominator as closingRate. Always <= closingRate. */
    issuedClosingRate?: number;
  } => {
    if (input && typeof input === 'object') {
      const obj = input as Record<string, unknown>;
      if (obj.data && typeof obj.data === 'object') {
        return obj.data as {
          totalLeads?: number;
          leadsByStatus?: Record<string, { count?: number }>;
          closingRate?: number;
          leadsWithConversations?: number;
          issuedClosingRate?: number;
        };
      }
      return obj as unknown as {
        totalLeads?: number;
        leadsByStatus?: Record<string, { count?: number }>;
        closingRate?: number;
        leadsWithConversations?: number;
    /** ISSUED only, same denominator as closingRate. Always <= closingRate. */
    issuedClosingRate?: number;
      };
    }
    return {};
  };
  const payload = unwrap(stats);
  const totalLeads: number = payload.totalLeads ?? 0;
  const leadsByStatus: Record<string, { count?: number }> = payload.leadsByStatus ?? {};
  // Close Rate = PRODUCTION: (issued + underwriting) ÷ (issued + underwriting + lost).
  // Identical definition to the agency dashboard and the leaderboard, so one agent cannot
  // show different close rates on different screens. The issued-only figure is available
  // on the payload as `issuedClosingRate` but is deliberately not surfaced here — this
  // card is a compact summary, and the agency dashboard is where the pair is shown.
  //
  // ⚠️ NOT `productionClosingRate` — that field is the APPOINTMENT production rate, a
  // different denominator entirely, and was briefly shown here as if it were this number.
  const closingRate: number = payload.closingRate ?? 0;

  // Calculate total conversions first (Underwriting + Sold & Approved)
  const underwritingCount = leadsByStatus['Underwriting']?.count || 0;
  const soldCount = leadsByStatus['Sold & Approved']?.count || 0;
  const totalConversions = underwritingCount + soldCount;

  // Exclude Bad number and Do not call from conversion rate denominator
  const badNumberCount = leadsByStatus['Bad number']?.count || 0;
  const doNotCallCount = leadsByStatus['Do not call']?.count || 0;
  const validLeadsForConversion = totalLeads - badNumberCount - doNotCallCount;

  // Conversion rate: (Underwriting + Sold & Approved) / Valid Leads (excluding Bad number & DNC)
  const conversionRate = validLeadsForConversion > 0 ? ((totalConversions / validLeadsForConversion) * 100) : 0;

  // Build status data using our configuration
  // For Underwriting and Approved, show conversion rate (relative to total conversions)
  // For other statuses, show distribution rate (relative to total leads)
  const statusData = LEAD_STATUS_CONFIG.map(({ apiKey, label, color }) => {
    const statusInfo = leadsByStatus[apiKey];
    const count = statusInfo?.count || 0;

    // For Underwriting and Approved, calculate percentage from total conversions
    // For others, calculate from total leads
    let percentage: string;
    if (apiKey === 'Underwriting' || apiKey === 'Sold & Approved') {
      percentage = totalConversions > 0 ? ((count / totalConversions) * 100).toFixed(1) : "0.0";
    } else {
      percentage = totalLeads > 0 ? ((count / totalLeads) * 100).toFixed(1) : "0.0";
    }

    return {
      label,
      count,
      color,
      percentage,
    };
  });

  // Add custom pipeline stages (non-system) after system statuses
  const systemNames = LEAD_STATUS_CONFIG.map(s => s.apiKey.toLowerCase());
  const customStages = (pipelineStages || [])
    .filter(s => !s.isSystem && s.isVisible && !systemNames.includes(s.name.toLowerCase()))
    .map(stage => {
      const count = leadsByStatus[stage.name]?.count || 0;
      const percentage = totalLeads > 0 ? ((count / totalLeads) * 100).toFixed(1) : "0.0";
      return {
        label: stage.name,
        count,
        color: `text-white`,
        customColor: stage.color || '#6b7280',
        percentage,
      };
    });


  return (
    <Card className="shadow-sm border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Leads Overview
        </CardTitle>
        <TrendingUp className="h-5 w-5 text-primary" />
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <TooltipProvider>
          <div className="flex flex-col gap-2">
            <div className="text-center p-2 bg-gradient-to-br from-primary/10 to-primary/20 rounded-lg border border-primary/20 flex items-center justify-between">
              <span className="text-xs text-primary font-medium">Total Leads</span>
              <span className="text-base font-bold text-primary">{totalLeads >= 1000000 ? `${(totalLeads / 1000000).toFixed(1)}M` : totalLeads >= 10000 ? `${(totalLeads / 1000).toFixed(0)}k` : totalLeads.toLocaleString()}</span>
            </div>
            <div className={`text-center p-2 bg-gradient-to-br ${isWhiteLabeled ? 'from-secondary/10 to-secondary/20 border-secondary/20' : 'from-orange-50 to-orange-100 border-orange-200'} rounded-lg border flex items-center justify-between relative`}>
              <div className="flex items-center gap-1">
                <span className={`text-xs font-medium ${isWhiteLabeled ? 'text-secondary' : 'text-orange-600'}`}>Conv Rate</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className={`h-3 w-3 cursor-help ${isWhiteLabeled ? 'text-secondary/60' : 'text-orange-400'}`} />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px]">
                    <p className="text-xs">(Underwriting + Sold & Approved) / Total Leads (excl. Bad # & DNC)</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className={`text-base font-bold ${isWhiteLabeled ? 'text-secondary' : 'text-orange-600'}`}>{conversionRate.toFixed(1)}%</span>
            </div>
            <div className={`text-center p-2 bg-gradient-to-br ${isWhiteLabeled ? 'from-primary/10 to-primary/20 border-primary/20' : 'from-green-50 to-green-100 border-green-200'} rounded-lg border flex items-center justify-between relative`}>
              <div className="flex items-center gap-1">
                <span className={`text-xs font-medium ${isWhiteLabeled ? 'text-primary' : 'text-green-600'}`}>Close Rate</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className={`h-3 w-3 cursor-help ${isWhiteLabeled ? 'text-primary/60' : 'text-green-400'}`} />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px]">
                    <p className="text-xs">Your own leads only. Sold and approved plus underwriting, out of leads that reached a decision.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className={`text-base font-bold ${isWhiteLabeled ? 'text-primary' : 'text-green-600'}`}>{closingRate.toFixed(1)}%</span>
            </div>
          </div>
        </TooltipProvider>

        {/* Status Breakdown */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900 text-sm">
            Lead Status Breakdown
          </h4>
          {totalLeads === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <p className="text-sm">No leads data available</p>
            </div>
          ) : (
            <>
              {/* System statuses — always visible */}
              <div className="space-y-2">
                {statusData.map((status) => (
                  <div
                    key={status.label}
                    className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge className={`${status.color} font-medium text-xs flex-shrink-0`}>
                        {status.count.toLocaleString()}
                      </Badge>
                      <span className="text-xs font-medium text-gray-700 truncate">
                        {status.label}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 font-medium">
                      {status.percentage}%
                    </span>
                  </div>
                ))}
              </div>
              {/* Custom pipeline stages — scrollable */}
              {customStages.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                    {customStages.map((stage) => (
                      <div
                        key={stage.label}
                        className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge
                            className="text-white font-medium text-xs flex-shrink-0"
                            style={{ backgroundColor: stage.customColor }}
                          >
                            {stage.count.toLocaleString()}
                          </Badge>
                          <span className="text-xs font-medium text-gray-700 truncate">
                            {stage.label}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 font-medium">
                          {stage.percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

      </CardContent>
    </Card>
  );
};